import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import { Map, Marker } from "maplibre-gl";
import { FeatureCollection, Geometry } from "geojson";

import { useAdminAreaById } from "@/api/adminArea/userAdminAreaAPI";
import {
  CIRCE_RADIUS_SOURCE_ID,
  COUNTRY_SOURCE_ID,
  GLOBAL_ADMIN_SOURCE_ID,
  ROUTING_SOURCE_ID
} from "@/constants/map";
import { getValueOfHighestLevelAdminArea } from "@/helpers/datasetAttributeHelper";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import { setAdminAreaSettingsData } from "@/store/slice/spatialSlice";
import { MapStyleProperties } from "@/types/map";
import { AreaInputType } from "@/types/spatial";
import {
  hideAllLayersWithPrefixes,
  removeAllSourceLayerWithPrefix,
  showLayersWithLayerIds
} from "@/utils/map";

type UseAdminAreaProps = {
  map: Map;
  isMapLoaded: boolean;
  markers: Marker[];
  zoomLevel: number;
  removeCatchmentMapDisplay: () => void;
  handleAddRegionLayer: (
    sourceId: string,
    polygonData: FeatureCollection<Geometry>,
    styleProperties?: MapStyleProperties
  ) => void;
  removeAllMarkers: () => void;
};

export const useAdminArea = ({
  map,
  isMapLoaded,
  markers,
  zoomLevel,
  removeCatchmentMapDisplay,
  handleAddRegionLayer,
  removeAllMarkers
}: UseAdminAreaProps) => {
  const { isAdminTreeEnabled } = useAppFeatures();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { datasets } = useSelector((state: RootState) => state.dataset);
  const {
    spatialConfig: { areaInput: spatialAreaInputType },
    adminAreaSettingsData,
    adminAreaSettingsDataTemp,
    adminAreaSettings
  } = useSelector((state: RootState) => state.spatial);
  const {
    showAreaAdminPopup,
    showAnalysisResultPopup,
    showComparisonPopup,
    showAnalysisSummaryPopup,
    showSearchLocationPopup
  } = useSelector((state: RootState) => state.ui);

  const [adminAreaId, setAdminAreaId] = useState<string | null>(null);
  const [hiddenLayerIds, setHiddenLayerIds] = useState<string[]>([]);

  const isPreviewMode = searchParams.get("preview") === "true";

  const {
    data: adminAreaDataById = null,
    isLoading,
    isError
  } = useAdminAreaById(adminAreaId, undefined, {
    enabled: !showAreaAdminPopup && !isAdminTreeEnabled
  });

  const handleAddAdminAreaRegion = () => {
    removeAllSourceLayerWithPrefix(map, COUNTRY_SOURCE_ID, []);
    removeCatchmentMapDisplay();
    if (!adminAreaSettingsData?.data) return;

    const dataCollection = adminAreaSettingsData.data
      ?.map((item) => item.boundary)
      ?.flat();
    dataCollection?.forEach((item, index) => {
      handleAddRegionLayer(`${COUNTRY_SOURCE_ID}-${index}`, item);
    });
  };

  const handleAddAdminAreaRegionTemp = () => {
    if (
      !isMapLoaded ||
      !showAreaAdminPopup ||
      spatialAreaInputType?.value === AreaInputType.CATCHMENT ||
      (adminAreaSettingsDataTemp?.data === adminAreaSettingsData?.data &&
        map?.getLayer(COUNTRY_SOURCE_ID))
    ) {
      return;
    }
    removeAllSourceLayerWithPrefix(map, COUNTRY_SOURCE_ID, []);
    removeCatchmentMapDisplay();

    if (!adminAreaSettingsDataTemp?.data) return;

    const dataCollection = adminAreaSettingsDataTemp.data
      ?.map((item) => item.boundary)
      ?.flat();
    dataCollection?.forEach((item, index) => {
      handleAddRegionLayer(`${COUNTRY_SOURCE_ID}-${index}`, item);
    });
  };

  useEffect(() => {
    if (!isLoading && !isError && !isAdminTreeEnabled) {
      dispatch(setAdminAreaSettingsData(adminAreaDataById));
    }
  }, [adminAreaDataById, dispatch, isError, isLoading, isAdminTreeEnabled]);

  // Check the zoom level has changed, will refetch admin area data
  useEffect(() => {
    // Check if admin area popup is open and value of admin area id is null will passing
    if (
      adminAreaSettings === null ||
      showAreaAdminPopup ||
      showAnalysisResultPopup ||
      showComparisonPopup.isShow ||
      spatialAreaInputType?.value === AreaInputType.CATCHMENT ||
      spatialAreaInputType?.value === AreaInputType.POLYGON
    ) {
      if (adminAreaId) setAdminAreaId(null); // reset value of admin area id
      return;
    }
    const adminAreaIdHighest =
      getValueOfHighestLevelAdminArea(adminAreaSettings);
    if (!adminAreaIdHighest) return;
    setAdminAreaId(adminAreaIdHighest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    zoomLevel,
    adminAreaSettings,
    showAreaAdminPopup,
    showAnalysisResultPopup,
    showComparisonPopup,
    spatialAreaInputType
  ]);

  // TODO: Refactor circular dependency and unclear intent
  // Repetitive pattern with handleAddAdminAreaRegion
  useEffect(() => {
    if (
      !isMapLoaded ||
      showAreaAdminPopup ||
      showAnalysisResultPopup ||
      showAnalysisSummaryPopup ||
      showComparisonPopup.isShow ||
      spatialAreaInputType?.value === AreaInputType.CATCHMENT ||
      isPreviewMode ||
      showSearchLocationPopup
    ) {
      return;
    }
    handleAddAdminAreaRegion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMapLoaded,
    showAreaAdminPopup,
    markers,
    spatialAreaInputType,
    adminAreaSettingsData,
    showAnalysisSummaryPopup,
    showAnalysisResultPopup,
    showComparisonPopup,
    isPreviewMode,
    showSearchLocationPopup
  ]);

  // TODO: Refactor circular dependency and unclear intent
  // Repetitive pattern with handleAddAdminAreaRegionTemp
  useEffect(() => {
    handleAddAdminAreaRegionTemp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMapLoaded,
    showAreaAdminPopup,
    spatialAreaInputType,
    adminAreaSettingsData,
    adminAreaSettingsDataTemp
  ]);

  useEffect(() => {
    if (!isMapLoaded) return;
    if (showSearchLocationPopup) {
      const prefixes = [
        COUNTRY_SOURCE_ID,
        ROUTING_SOURCE_ID,
        CIRCE_RADIUS_SOURCE_ID,
        GLOBAL_ADMIN_SOURCE_ID
      ];
      Object.keys(datasets).forEach((datasetId) => {
        prefixes.push(datasetId);
      });
      const hiddenLayerIds = hideAllLayersWithPrefixes(map, prefixes);
      removeAllMarkers();
      setHiddenLayerIds(hiddenLayerIds);
    } else {
      showLayersWithLayerIds(map, hiddenLayerIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMapLoaded, showSearchLocationPopup]);

  return {};
};

export default useAdminArea;
